import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { DropTarget } from 'react-dnd'
import { injectIntl } from 'react-intl'
import _ from 'lodash'
import log from 'loglevel'

import config from '@config'
import { ACTION_CD, MODULE_CD } from '../../../../../../services/logAction/constants'
import hocLogAction from '../../../../../../services/logAction'

import { DND_TYPE } from '../../../../../../dnd/constants'

import ScrollableContent from '../../../../components/Workspace/components/ScrollableContent'
import LineBanner from '../../../../../../components/Banners/components/LineBanner'
import ConnectionChecker from '../../../../../../components/ConnectionChecker'

import CarouselBlock from './components/CarouselBlock'
import NoBlock from './components/NoBlock'
import Header from './components/Header'
import Empty from './components/Empty'

import { LOCALE_PREFIX } from './constants'
import hocConnect from './hocConnect'

import './style.scss'

const MODULE_NAME = config.modules.portfolio.key
const BLOCKS = config.modules.portfolio.blocks

const blockAccessRightsPathFn = (state, blockKey) => {
    const activePortfolioType = state.getIn(['ui', 'modules', MODULE_NAME, 'ribbon', 'portfolios', 'active'], null)
    return [
        'authorization',
        'accessRights',
        MODULE_NAME,
        'custom',
        'portfolios',
        activePortfolioType,
        'blocks',
        blockKey,
    ]
}

const blockComponents = _.mapValues(BLOCKS, (block) => {
    let component = null
    try {
        // eslint-disable-next-line import/no-dynamic-require, global-require
        component = require(`./components/Blocks/${block.component}`).default
    } catch (error) {
        log.warn(`Missing Portfolio Workspace Block component ${block.key}`)
    }

    return component
})

const workspaceTarget = {
    drop: (props, monitor) => {
        const { openPortfolioById } = props

        const group = monitor.getItem()

        openPortfolioById(group.groupId)
    },
}

function collect(connect) {
    return {
        connectDropTarget: connect.dropTarget(),
    }
}

@injectIntl
@hocLogAction
@hocConnect
@DropTarget(DND_TYPE.GROUP, workspaceTarget, collect)
export default class Workspace extends Component {
    static propTypes = {
        activePortfolioId: PropTypes.string,
        banner: PropTypes.shape({
            line: PropTypes.bool.isRequired,
            carousel: PropTypes.bool.isRequired,
        }),
        connectDropTarget: PropTypes.func.isRequired,
        intl: PropTypes.object.isRequired,
        logAction: PropTypes.func.isRequired,
        noBlockShown: PropTypes.bool.isRequired,
        openPortfolioById: PropTypes.func.isRequired,
        portfolioData: PropTypes.object.isRequired,
        queryStringId: PropTypes.string,
        sortedBlocks: PropTypes.array.isRequired,
    }

    static defaultProps = {
        activePortfolioId: null,
        queryStringId: null,
    }

    constructor(props) {
        super(props)

        this._blocks = {}
        this._bannerButtonsSetting = [
            {
                text: props.intl.formatMessage({ id: `${LOCALE_PREFIX}.banner.line.button.getMore` }),
                onClick: this.onLineClick,
            },
        ]
    }

    componentDidMount() {
        const { openPortfolioById, queryStringId } = this.props

        if (queryStringId) openPortfolioById(queryStringId)
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.activePortfolioId !== nextProps.activePortfolioId) return true
        if (!_.isEqual(this.props.portfolioData, nextProps.portfolioData)) return true
        if (!_.isEqual(this.props.sortedBlocks, nextProps.sortedBlocks)) return true
        if (this.props.noBlockShown !== nextProps.noBlockShown) return true
        if (this.props.queryStringId !== nextProps.queryStringId) return true

        return false
    }

    onLineClick = () => {
        const { logAction } = this.props

        logAction({
            actionCd: ACTION_CD.UPSELL_CLICK,
            moduleCd: MODULE_CD.PORTFOLIO,
            note: 'inlineBanner',
            parameters: {
                onClick: 'inlineBanner',
            },
        })
        window.open('https://www.dnb.com/cs-cz/produkty/gnosus/')
    }

    onCarouselClick = () => {
        const { logAction } = this.props

        logAction({
            actionCd: ACTION_CD.UPSELL_CLICK,
            moduleCd: MODULE_CD.PORTFOLIO,
            note: 'carouselBanner',
            parameters: {
                onClick: 'carouselBanner',
            },
        })
        window.open('https://source.bisnode.cz/data/gnosus/Gnosus_Portfolio_Premium-ukazka.pdf')
    }

    renderLineBanner = () => {
        const { banner, intl } = this.props

        if (!banner.line) return null

        return (
            <div key={'line-banner'} className={'portfolio-line-banner'}>
                <LineBanner
                    id={'portfolio-line'}
                    text={intl.formatMessage({ id: `${LOCALE_PREFIX}.banner.line.text` })}
                    buttonsSetting={this._bannerButtonsSetting}
                    style={{ marginBottom: '25px' }}
                />
            </div>
        )
    }

    renderCarouselBanner = () => {
        const { banner } = this.props

        if (!banner.carousel) return null

        return (
            <div key={'carousel-banner'} className={'portfolio-carousel-banner'}>
                <CarouselBlock onClick={this.onCarouselClick} />
            </div>
        )
    }

    renderBlocks = () => {
        const { sortedBlocks, activePortfolioId } = this.props

        return _.reduce(
            sortedBlocks,
            (acc, blockKey) => {
                const BlockComponent = blockComponents[blockKey]

                if (!BlockComponent) return acc

                return [
                    ...acc,
                    <div key={blockKey} className={'block-wrap'} ref={(r) => (this._blocks[blockKey] = r)}>
                        <BlockComponent
                            blockKey={blockKey}
                            id={activePortfolioId}
                            blockAccessRightsPathFn={blockAccessRightsPathFn}
                        />
                    </div>,
                ]
            },
            []
        )
    }

    renderContent = () => {
        const { noBlockShown } = this.props

        if (noBlockShown) return <NoBlock />

        return [this.renderLineBanner(), this.renderBlocks(), this.renderCarouselBanner()]
    }

    renderPortfolio = () => {
        const { activePortfolioId, portfolioData } = this.props

        return (
            <div className={`portfolio id-${activePortfolioId}`}>
                <div className={'header'}>
                    <Header
                        name={portfolioData.name}
                        count={portfolioData.count}
                        naturalPerson={portfolioData.naturalPerson}
                        foreign={portfolioData.foreign}
                        outOfBusiness={portfolioData.outOfBusiness}
                    />
                </div>
                <div className={'content'}>
                    <ScrollableContent id={`${MODULE_NAME}_${activePortfolioId}`} scrollRefs={this._blocks}>
                        {this.renderContent()}
                    </ScrollableContent>
                </div>
            </div>
        )
    }

    render() {
        const { activePortfolioId, connectDropTarget } = this.props

        const content = activePortfolioId !== null ? this.renderPortfolio() : <Empty />

        return connectDropTarget(
            <div className={`workspace-${MODULE_NAME}`}>
                {content}
                <ConnectionChecker id={'portfolio-connection-checker'} />
            </div>
        )
    }
}
